'use client';

import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, Warning, Info, XCircle } from '@phosphor-icons/react';
import { cn } from '../../utils/cn';

// Toast Types
export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  title?: string;
  description?: string;
  type?: ToastType;
  duration?: number;
  action?: React.ReactNode;
}

// Toast Context
interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  updateToast: (id: string, toast: Partial<Toast>) => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

// Toast Provider
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const addToast = React.useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { ...toast, id }]);
    return id;
  }, []);

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const updateToast = React.useCallback((id: string, toast: Partial<Toast>) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...toast } : t))
    );
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, updateToast }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        <ToastViewport />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

// useToast Hook
export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// Toast Icons
const toastIcons = {
  success: CheckCircle,
  error: XCircle,
  warning: Warning,
  info: Info,
};

const toastStyles = {
  success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  error: 'bg-red-50 border-red-200 text-red-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  info: 'bg-blue-50 border-blue-200 text-blue-900',
};

const toastIconColors = {
  success: 'text-emerald-500',
  error: 'text-red-500',
  warning: 'text-amber-500',
  info: 'text-blue-500',
};

// Individual Toast Component
const ToastItem = React.forwardRef<HTMLDivElement, {
  toast: Toast;
  onRemove: (id: string) => void;
}>(function ToastItem({ toast, onRemove }, ref) {
  const { id, title, description, type = 'info', duration = 5000 } = toast;
  const Icon = toastIcons[type];

  return (
    <ToastPrimitive.Root
      duration={duration}
      onOpenChange={(open) => {
        if (!open) onRemove(id);
      }}
      asChild
    >
      <motion.div
        ref={ref}
        layout
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, x: 100, scale: 0.9 }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30,
        }}
        className={cn(
          'relative flex w-full items-center gap-3 overflow-hidden rounded-2xl border p-4 shadow-lg shadow-black/5',
          toastStyles[type]
        )}
      >
        {/* Icon */}
        <div className={cn('flex-shrink-0', toastIconColors[type])}>
          <Icon className="h-5 w-5" weight="fill" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && (
            <ToastPrimitive.Title className="text-sm font-semibold">
              {title}
            </ToastPrimitive.Title>
          )}
          {description && (
            <ToastPrimitive.Description className="mt-1 text-sm opacity-90">
              {description}
            </ToastPrimitive.Description>
          )}
        </div>

        {/* Action */}
        {toast.action && (
          <div className="flex-shrink-0">{toast.action}</div>
        )}

        {/* Close Button */}
        <ToastPrimitive.Close asChild>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex-shrink-0 rounded-lg p-1.5 opacity-60 transition-opacity hover:opacity-100 hover:bg-black/5"
          >
            <X className="h-4 w-4" />
          </motion.button>
        </ToastPrimitive.Close>

        {/* Progress Bar */}
        <motion.div
          className={cn(
            'absolute bottom-0 left-0 h-0.5',
            toastIconColors[type].replace('text-', 'bg-')
          )}
          initial={{ width: '100%' }}
          animate={{ width: '0%' }}
          transition={{ duration: duration / 1000, ease: 'linear' }}
        />
      </motion.div>
    </ToastPrimitive.Root>
  );
});

// Toast Viewport
function ToastViewport() {
  const { toasts, removeToast } = useToast();

  return (
    <ToastPrimitive.Viewport
      className={cn(
        'fixed bottom-0 right-0 z-[100] flex w-full flex-col gap-3 p-6',
        'max-w-[420px]'
      )}
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </AnimatePresence>
    </ToastPrimitive.Viewport>
  );
}

// Convenience hooks for common toast types
export function useToastActions() {
  const { addToast, removeToast } = useToast();

  return {
    success: (description: string, title?: string) =>
      addToast({ type: 'success', title: title || '成功', description }),
    error: (description: string, title?: string) =>
      addToast({ type: 'error', title: title || '错误', description }),
    warning: (description: string, title?: string) =>
      addToast({ type: 'warning', title: title || '警告', description }),
    info: (description: string, title?: string) =>
      addToast({ type: 'info', title: title || '提示', description }),
    custom: (toast: Omit<Toast, 'id'>) => addToast(toast),
    remove: removeToast,
  };
}
